import { ChevronLeft, ChevronRight, Loader2, X } from "@digital-twins/design-system/icons";
import { useTranslation } from "react-i18next";
import useSWR from "swr";

import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ReelPlayer } from "@digital-twins/deck-ui/components/reel/ReelPlayer";
import { fetchStudyReel, studyReelKey } from "./studyReelData";
import { useReelPlayerLabels } from "./useReelPlayerLabels";

export interface TheaterReel {
  token: string;
  studyName: string;
}

export interface StudyReelFullscreenProps {
  reels: Array<TheaterReel>;
  /** The reel showing; null when the theater is closed. */
  index: number | null;
  onIndexChange: (index: number | null) => void;
  onOpenReport: (token: string) => void;
}

/**
 * The rail's reels watched like stories: edge to edge on a phone, window-tall
 * on a dark backdrop on desktop. No buttons on the reel: the middle plays or
 * pauses, the sides turn pages. Desktop moves between reels with the arrows
 * (or up/down keys), a phone with a vertical swipe.
 */
export function StudyReelFullscreen(props: StudyReelFullscreenProps) {
  const { reels, index, onIndexChange, onOpenReport } = props;

  const { t } = useTranslation("app");

  const labels = useReelPlayerLabels();

  const current = index === null ? null : (reels[index] ?? null);

  const { data: reel, error } = useSWR(current ? studyReelKey(current.token) : null, fetchStudyReel, {
    revalidateOnFocus: false,
  });

  const hasPrevious = index !== null && index > 0;
  const hasNext = index !== null && index < reels.length - 1;

  const onStep = (direction: 1 | -1) => {
    if (index === null) {
      return;
    }
    const target = index + direction;
    if (target >= 0 && target < reels.length) {
      onIndexChange(target);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      onStep(1);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      onStep(-1);
    }
  };

  const stepButtonClass =
    "hidden size-12 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition-opacity hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:pointer-events-none disabled:opacity-0 sm:flex";

  const body =
    current && reel ? (
      <ReelPlayer
        key={current.token}
        reel={reel}
        labels={labels}
        onOpenReport={() => onOpenReport(current.token)}
        controls="none"
        density="short"
        tapZones="theater"
        onSwipeVertical={onStep}
        autoPlay
        autoFocus
        isFlat
      />
    ) : error ? (
      <p className="text-center text-sm text-white/70" data-testid="study-reel-fullscreen-error">
        {t("studyLobby.reel.loadFailed")}
      </p>
    ) : (
      <div className="flex aspect-[9/16] w-full items-center justify-center">
        <Loader2 className="size-6 animate-spin text-white/70" aria-label={t("studyLobby.reel.loading")} />
      </div>
    );

  return (
    <Dialog open={current !== null} onOpenChange={(isOpen) => !isOpen && onIndexChange(null)}>
      <DialogContent
        hideClose
        aria-describedby={undefined}
        onKeyDown={onKeyDown}
        className="left-0 top-0 flex h-[100dvh] max-h-none w-screen max-w-none translate-x-0 translate-y-0 items-center justify-center gap-6 rounded-none border-0 bg-black p-0 shadow-none sm:p-0 data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0"
        data-testid="study-reel-fullscreen"
      >
        <DialogTitle className="sr-only">
          {t("studyLobby.reel.dialogTitle", { name: current?.studyName ?? "" })}
        </DialogTitle>
        <button
          type="button"
          className={stepButtonClass}
          aria-label={t("studies.reelRail.previous")}
          disabled={!hasPrevious}
          onClick={() => onStep(-1)}
          data-testid="study-reel-fullscreen-previous"
        >
          <ChevronLeft className="size-6" aria-hidden />
        </button>
        <div className="w-[min(100vw,calc(100dvh*9/16))] sm:w-[min(calc(100vw-160px),calc((100dvh-32px)*9/16))]">
          {body}
        </div>
        <button
          type="button"
          className={stepButtonClass}
          aria-label={t("studies.reelRail.next")}
          disabled={!hasNext}
          onClick={() => onStep(1)}
          data-testid="study-reel-fullscreen-next"
        >
          <ChevronRight className="size-6" aria-hidden />
        </button>
        <DialogClose
          className="absolute right-4 top-4 z-10 flex size-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          aria-label={t("studyLobby.reel.close")}
          data-testid="study-reel-fullscreen-close"
        >
          <X className="size-5" aria-hidden />
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
